'use client'

import { useCallback, useContext, useEffect, useState } from 'react'
import { MdCheck, MdDoneAll, MdNotificationsActive, MdOpenInNew } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'

import {
    getNotifications,
    getUnreadCount,
    markAllAsRead,
    markAsRead,
    type Notification,
} from '@/lib/api/notifications/notification-api'
import { useChatOverlay } from '@/lib/hooks/use-chat-overlay'
import { SocketContext } from '@/lib/providers/socket-provider'
import { useAnchoredDropdown } from '@/components/use-anchored-dropdown'

import MyAlertsModal from './my-alerts-modal'
import MyAlertsPreferences from './my-alerts-preferences'
import { useMessageAlertSound } from './use-message-alert-sound'

/**
 * "Meus Alertas" — o que é endereçado a ESTA pessoa.
 *
 * Existe porque o sino da navbar virou inútil: com dezenas de avisos de operação por
 * hora, ninguém achava os próprios. A separação é no servidor
 * (`Notification.scope`), não aqui — filtrar no cliente mentiria sobre o total e o
 * contador continuaria somando o que não interessa.
 *
 * O que cai aqui: lead atribuído, conversa transferida, cliente que entrou na carteira,
 * oportunidade que mudou de etapa, passo de notificação dos fluxos e "N novas mensagens
 * de Fulano" — este último agrupado por conversa, uma linha só.
 *
 * O botão colado à direita escolhe quais desses tipos chegam (`MyAlertsPreferences`)
 * e liga o som de mensagem nova (`useMessageAlertSound`). O som mora aqui porque este
 * componente fica no navbar, montado em qualquer tela do sistema.
 */
const PREVIEW_SIZE = 8

export default function MyAlertsDropdown() {
    const navigate = useNavigate()
    const openChatOverlay = useChatOverlay()
    const socketContext = useContext(SocketContext)
    const [items, setItems] = useState<Notification[]>([])
    const [unread, setUnread] = useState(0)
    const [showAll, setShowAll] = useState(false)
    const [open, setOpen] = useState(false)
    const sound = useMessageAlertSound(socketContext?.socket)

    /**
     * Portal no `body`, e não o `dropdown` do DaisyUI.
     *
     * O dock de atendimento e o menu lateral criam CONTEXTOS DE EMPILHAMENTO: dentro
     * deles nenhum `z-index` local resolve, e o painel abria por baixo dos dois. É a
     * mesma armadilha já registrada no `CLAUDE.md` para o seletor de emoji — ali a
     * solução também foi sair por portal.
     */
    const { anchorRef, renderPanel } = useAnchoredDropdown(
        open,
        () => setOpen(false),
        // O gatilho é um BOTÃO, não um campo: herdar a largura dele espremeria a lista
        // e criaria rolagem horizontal. A largura vem do `w-96` do painel.
        { matchAnchorWidth: false },
    )

    const load = useCallback(async () => {
        const [list, count] = await Promise.all([
            getNotifications({ scope: 'PERSONAL', page: 1, limit: PREVIEW_SIZE }),
            getUnreadCount('PERSONAL'),
        ])
        if (list.success) setItems(list.result?.notifications ?? [])
        if (count.success) setUnread(count.result?.count ?? 0)
    }, [])

    useEffect(() => {
        void load()
    }, [load])

    // Tempo real: o backend já emite nos dois eventos, e o alerta agregado de mensagem
    // reaproveita `notification:new` mesmo quando ATUALIZA a linha em vez de criar —
    // para a tela é fato novo, e é isso mesmo.
    useEffect(() => {
        const socket = socketContext?.socket
        if (!socket) return
        const refresh = () => void load()
        socket.on('notification:new', refresh)
        socket.on('notification:unread-count', refresh)
        return () => {
            socket.off('notification:new', refresh)
            socket.off('notification:unread-count', refresh)
        }
    }, [socketContext?.socket, load])

    /** Dá por lida sem abrir — o painel fica aberto para a pessoa seguir limpando. */
    const readOne = async (item: Notification) => {
        await markAsRead(item.id)
        void load()
    }

    const readAll = async () => {
        await markAllAsRead()
        void load()
    }

    const openItem = async (item: Notification) => {
        setOpen(false)
        if (!item.readAt) {
            await markAsRead(item.id)
            void load()
        }
        // Alerta de conversa abre SOBRE a tela atual (dock), sem tirar a pessoa do
        // que estava fazendo. Fora do desktop, ou já na tela cheia de atendimento, o
        // hook recusa e a navegação de sempre assume.
        if (!item.link) return
        if (openChatOverlay(item.link)) return
        navigate(item.link)
    }

    return (
        <>
            {/* Os dois botões formam um só controle: abrir os alertas e escolher quais
                chegam. Cantos retos onde se encostam, para não parecerem soltos. */}
            <div className="flex items-stretch">
                <div ref={anchorRef} className="relative">
                    <button
                        type="button"
                        onClick={() => setOpen(value => !value)}
                        // `primary` (o roxo da marca) e não `secondary`: o azul já é a cor de
                        // "Solicitações" ao lado, e dois botões azuis acesos ao mesmo tempo
                        // não se distinguem de relance — que é o único momento em que alguém
                        // olha para a barra.
                        className={`btn gap-2 rounded-r-none ${unread > 0 ? 'btn-primary !text-white' : 'btn-ghost'}`}
                        title="Meus Alertas"
                    >
                        <MdNotificationsActive className="text-xl" />
                        <span className="hidden lg:inline">Meus Alertas</span>
                        {/* Contador DENTRO do botão: no canto (indicator) ele ficava por
                            cima da emenda com o botão de preferências ao lado. */}
                        {unread > 0 && (
                            <span className="badge badge-warning badge-sm">
                                {unread > 99 ? '99+' : unread}
                            </span>
                        )}
                    </button>
                </div>
                <MyAlertsPreferences
                    highlighted={unread > 0}
                    soundEnabled={sound.enabled}
                    soundSaving={sound.saving}
                    onToggleSound={() => void sound.toggle()}
                />
            </div>

            {renderPanel(
                // `flex-nowrap` é obrigatório: o `.menu` do DaisyUI nasce com
                // `flex-flow: column wrap`, e o painel do portal já tem altura máxima
                // própria. Com a lista cheia, o que não cabe QUEBRA PARA UMA SEGUNDA
                // COLUNA — o sintoma é rolagem horizontal no rodapé e alertas do topo
                // aparecendo ao lado. A altura e a rolagem vertical são do painel, por
                // isso aqui não entra `max-h`: duas restrições de altura é o que
                // dispara a quebra.
                <ul className="menu flex-nowrap w-96 p-2">
                    {unread > 0 && (
                        <li className="border-b border-base-200 mb-1 pb-1">
                            <button
                                type="button"
                                className="justify-end gap-2 text-xs py-1"
                                onClick={() => void readAll()}
                            >
                                <MdDoneAll /> Marcar todas como lidas
                            </button>
                        </li>
                    )}

                    {items.length === 0 ? (
                        <li className="px-3 py-6 text-center text-sm text-base-content/50">
                            Nenhum alerta para você.
                        </li>
                    ) : (
                        items.map(item => (
                            // `li` sem o estilo de item do menu: a linha tem DOIS botões
                            // (abrir e marcar como lida), e botão dentro de botão é HTML
                            // inválido — o navegador desfaz o aninhamento e o clique
                            // interno para de funcionar.
                            <li key={item.id} className="!block">
                                <div className="flex items-start gap-1 rounded hover:bg-base-200/60 px-2">
                                    <button
                                        type="button"
                                        className="flex flex-1 flex-col items-start gap-0.5 py-2 text-left min-w-0"
                                        onClick={() => void openItem(item)}
                                    >
                                        <span className="flex w-full items-center gap-2">
                                            {/* Não lida fica marcada: com a lista cheia,
                                                a diferença entre o que chegou agora e o
                                                que já foi visto é o que dá utilidade ao
                                                painel. */}
                                            {!item.readAt && (
                                                <span className="h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
                                            )}
                                            <span className="text-sm font-medium truncate">
                                                {item.title}
                                            </span>
                                        </span>
                                        <span className="text-xs text-base-content/60 line-clamp-2">
                                            {item.message}
                                        </span>
                                        <span className="text-[11px] text-base-content/40">
                                            {new Date(item.createdAt).toLocaleString('pt-BR')}
                                        </span>
                                    </button>

                                    {/* Dar por lido SEM abrir. Quem já sabe do que se
                                        trata pela linha não deveria precisar navegar para
                                        limpar o contador — é o que faz a pessoa desistir
                                        e ignorar o painel inteiro. */}
                                    {!item.readAt && (
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-xs btn-circle mt-2 flex-shrink-0"
                                            title="Marcar como lida"
                                            onClick={event => {
                                                event.stopPropagation()
                                                void readOne(item)
                                            }}
                                        >
                                            <MdCheck />
                                        </button>
                                    )}
                                </div>
                            </li>
                        ))
                    )}

                    <li className="border-t border-base-200 mt-1 pt-1">
                        <button
                            type="button"
                            className="justify-center gap-2 text-sm"
                            onClick={() => {
                                setOpen(false)
                                setShowAll(true)
                            }}
                        >
                            <MdOpenInNew /> Ver todas
                        </button>
                    </li>
                </ul>,
                'overflow-x-hidden',
            )}

            <MyAlertsModal
                isOpen={showAll}
                onClose={() => {
                    setShowAll(false)
                    void load()
                }}
            />
        </>
    )
}
